import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signOut, 
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { referralService } from '../services/referralService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Initialize Google Auth Provider
  const googleProvider = new GoogleAuthProvider();


  // Google sign-in function with popup fallback to redirect
  async function signInWithGoogle(referralCode = null) {
    try {
      // First try popup method
      try {
        const result = await signInWithPopup(auth, googleProvider);
        const user = result.user;
        
        // Check if user document exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // Create user document for new Google users
          const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          const userData = {
            uid: user.uid,
            email: user.email,
            firstName: firstName,
            lastName: lastName,
            phone: user.phoneNumber || '',
            address: '',
            membershipPlan: 'basic',
            createdAt: new Date().toISOString(),
            isEmailVerified: user.emailVerified,
            services: [],
            workshops: [],
            referrals: [],
            points: 0,
            provider: 'google',
            photoURL: user.photoURL
          };

          await setDoc(doc(db, 'users', user.uid), userData);

          // Process referral if referral code is provided
          if (referralCode) {
            try {
              await referralService.processReferral(referralCode, user.uid, {
                firstName,
                lastName,
                email: user.email
              });
              console.log('Referral processed successfully for code:', referralCode);
            } catch (referralError) {
              console.error('Error processing referral:', referralError);
              // Don't throw error here - user signup should still succeed even if referral fails
            }
          }
        }
        
        return result;
      } catch (popupError) {
        // If popup fails (blocked, etc.), fall back to redirect
        console.log('Popup failed, falling back to redirect:', popupError.message);
        
        // Store referral code in localStorage before redirect
        if (referralCode) {
          localStorage.setItem('hsp_referral_code', referralCode);
        }
        
        // Redirect to Google sign-in
        await signInWithRedirect(auth, googleProvider);
      }
    } catch (error) {
      throw error;
    }
  }

  // Handle redirect result and create user profile
  async function handleRedirectResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        const user = result.user;
        
        // Check if user document exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // Create user document for new Google users
          const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          const userData = {
            uid: user.uid,
            email: user.email,
            firstName: firstName,
            lastName: lastName,
            phone: user.phoneNumber || '',
            address: '',
            membershipPlan: 'basic',
            createdAt: new Date().toISOString(),
            isEmailVerified: user.emailVerified,
            services: [],
            workshops: [],
            referrals: [],
            points: 0,
            provider: 'google',
            photoURL: user.photoURL
          };

          await setDoc(doc(db, 'users', user.uid), userData);

          // Process referral if referral code was stored
          const storedReferralCode = localStorage.getItem('hsp_referral_code');
          if (storedReferralCode) {
            try {
              await referralService.processReferral(storedReferralCode, user.uid, {
                firstName,
                lastName,
                email: user.email
              });
              console.log('Referral processed successfully for code:', storedReferralCode);
              // Clear the stored referral code
              localStorage.removeItem('hsp_referral_code');
            } catch (referralError) {
              console.error('Error processing referral:', referralError);
              // Don't throw error here - user signup should still succeed even if referral fails
            }
          }
        }
        
        return result;
      }
    } catch (error) {
      console.error('Error handling redirect result:', error);
      throw error;
    }
  }

  // Sign out function
  async function logout() {
    try {
      await signOut(auth);
      setUserProfile(null);
    } catch (error) {
      throw error;
    }
  }


  // Update user profile
  async function updateUserProfile(uid, updates) {
    try {
      await setDoc(doc(db, 'users', uid), updates, { merge: true });
      // Refresh user profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
      }
    } catch (error) {
      throw error;
    }
  }

  // Get user profile from Firestore
  async function fetchUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch user profile when user signs in
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Handle redirect result on component mount
    handleRedirectResult();

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signInWithGoogle,
    logout,
    updateUserProfile,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
